import { defineStore } from 'pinia'
import { ChannelSession } from '@letsdial/api/src/lib/types/ChannelSession'
import useLetsDialAPI from '../services/letsDialAPI/useLetsDialAPI'
import {
  Call,
  Campaign,
  CampaignList,
  CampaignListRecord,
  Callback,
  Channel,
  ChannelConsumer,
  ChannelSessionEvent,
  ChannelSessionMessageEvent,
  FilterObject,
  JSONApiRelationData,
  Queue,
} from '@letsdial/api/src'
import { useAuthStore } from './authStore'
import { DateTime } from 'luxon'
import {
  VoicemailMessage,
  VoicemailMessageEvent,
} from '@letsdial/api/src/lib/types/VoicemailMessage'
import { useChatStore } from './chatStore'
import useGetStream from '../services/letsDialSocket/useGetStream'

let timedDataTimer: any
let authListener: any
let channelStreamClose: any
let voicemailMessagesStreamClose: any

export const useTaskStore = defineStore('tasks', {
  // unique id of the store across your application
  state: () =>
    ({
      channelSessions: [],
      channelIncludes: [],
      missedDispositions: [],
      missedDispositionIncludes: [],
      callbacks: [],
      callbacksIncludes: [],
      voicemailMessages: [],
      voicemailMessagesIncludes: [],
      expanded: false,
      unassignedVoicemailMessages: [],
      unassignedVoicemailMessagesIncludes: [],
    }) as {
      channelSessions: Array<ChannelSession>
      channelIncludes: Array<Queue | Channel | ChannelConsumer>
      missedDispositions: Array<Call>
      missedDispositionIncludes: Array<Campaign>
      callbacks: Array<Callback>
      callbacksIncludes: Array<CampaignListRecord | CampaignList>
      voicemailMessages: Array<VoicemailMessage>
      voicemailMessagesIncludes: Array<Queue | Call>
      unassignedVoicemailMessages: Array<VoicemailMessage>
      unassignedVoicemailMessagesIncludes: Array<Queue | Call>
      expanded: boolean
    },
  getters: {
    totalTasks(state) {
      return (
        state.channelSessions.length +
        state.missedDispositions.length +
        state.callbacks.length +
        state.voicemailMessages.length
      )
    },
    newAlerts(state) {
      const chatStore = useChatStore()
      return (
        state.channelSessions.reduce((prev, session) => {
          console.log('session', session.meta)
          prev += session?.meta?.unreadMessages?.length ?? 0
          return prev
        }, 0) + (chatStore.incomingChat ? 1 : 0)
      )
    },
    getChannelRelationship(state) {
      return (relationship: JSONApiRelationData | null | undefined) => {
        if (!relationship) {
          return undefined
        }
        return state.channelIncludes.find(
          (inc) => inc.type === relationship.type && inc.id === relationship.id,
        )
      }
    },
    getMissedDispositionRelationship(state) {
      return (relationship: JSONApiRelationData | null | undefined) => {
        if (!relationship) {
          return undefined
        }
        return state.missedDispositionIncludes.find(
          (inc) => inc.type === relationship.type && inc.id === relationship.id,
        )
      }
    },
    getCallbackRelationship(state) {
      return (relationship: JSONApiRelationData | null | undefined) => {
        if (!relationship) {
          return undefined
        }
        return state.callbacksIncludes.find(
          (inc) => inc.type === relationship.type && inc.id === relationship.id,
        )
      }
    },
    getVoicemailMessageRelationship(state) {
      return (relationship: JSONApiRelationData | null | undefined) => {
        if (!relationship) {
          return undefined
        }
        return state.voicemailMessagesIncludes.find(
          (inc) => inc.type === relationship.type && inc.id === relationship.id,
        )
      }
    },
    getUnassignedVoicemailMessageRelationship(state) {
      return (relationship: JSONApiRelationData | null | undefined) => {
        if (!relationship) {
          return undefined
        }
        return state.unassignedVoicemailMessagesIncludes.find(
          (inc) => inc.type === relationship.type && inc.id === relationship.id,
        )
      }
    },
    getUnassignedQueue() {
      return (voicemail: VoicemailMessage) => {
        const call = this.getUnassignedVoicemailMessageRelationship(
          voicemail.relationships.call.data,
        ) as Call
        if (call?.relationships?.queue?.data) {
          return this.getUnassignedVoicemailMessageRelationship(
            call?.relationships?.queue?.data,
          ) as Queue
        }
        return undefined
      }
    },
  },
  actions: {
    toggleExpanded() {
      this.expanded = !this.expanded
    },
    removeMissedDisposition(id: string | number) {
      this.missedDispositions = this.missedDispositions.filter(
        (d) => d.id !== id,
      )
    },
    mount() {
      console.log('TASK STORE MOUNT')
      const authStore = useAuthStore()
      if (authStore.user?.id && authStore.selectedOrganization?.id) {
        this.loadData()
      } else {
        authListener = authStore.$onAction(({ name, after }) => {
          after(() => {
            if (name === 'setSelectedOrganization') {
              this.loadData()
            }
          })
        })
      }
    },
    async loadData() {
      await this.loadChannelSessionData()
      this.reloadTimedData()
    },
    async addListeners() {
      const authStore = useAuthStore()
      if (authStore.selectedOrganization?.id) {
        const { leave: csClose } = useGetStream<
          ChannelSessionEvent | ChannelSessionMessageEvent
        >(
          'channel:stream',
          {
            filters: {
              userId: 'me',
              organizationId: authStore.selectedOrganization?.id,
            },
          },
          (event: ChannelSessionEvent | ChannelSessionMessageEvent) => {
            if (event.type === 'channelSessionMessages') {
              if (event.op === 'add') {
                if (event.data.userMessage) {
                  const session = this.channelSessions.find(
                    (session) => session.id === event.data.channelSessionId,
                  )
                  if (session) {
                    session.meta.unreadMessages.push(event.id)
                    session.meta.lastAction = DateTime.local().toSQL()
                  }
                }
              }
              if (event.op === 'replace' && event.data.readAt) {
                this.channelSessions.forEach((channelSession) => {
                  channelSession.meta.unreadMessages =
                    channelSession.meta.unreadMessages.filter(
                      (id) => id !== event.id,
                    )
                })
              }
            }
            if (event.type === 'channelSessions') {
              if (event.data.channelOutcomeId) {
                this.channelSessions = this.channelSessions.filter(
                  (session) => session.id !== event.id,
                )
              }
            }
          },
        )
        channelStreamClose = csClose
        if (authStore.user?.relationships.queues.data) {
          const { leave: vmClose } = useGetStream<VoicemailMessageEvent>(
            'voicemail:stream',
            {
              filters: {
                queueIds: authStore.user.relationships.queues.data
                  .map((q) => q.id)
                  .join(','),
              },
            },
            (event: VoicemailMessageEvent) => {
              if (event.data.userId) {
                this.unassignedVoicemailMessages =
                  this.unassignedVoicemailMessages.filter(
                    (vm) => vm.id !== event.id,
                  )
              }
            },
          )
          voicemailMessagesStreamClose = vmClose
        }
      }
    },
    async addSession(sessionId: number) {
      const api = useLetsDialAPI()
      const channelResult = await api.channelSessions.get(sessionId, {
        include: 'channel,consumer,channel.queue',
        fields: {
          channelSessions: 'additionalData,channel,consumer',
          channels: 'name,platform,queue',
          channelConsumers: 'additionalData,channelUserReference',
          queues: 'name',
        },
      })
      if (channelResult.data) {
        const session = channelResult.data
        if (!session.meta) {
          session.meta = {
            unreadMessages: [],
            lastAction: DateTime.local().toSQL(),
          }
        }
        this.channelSessions.push(session)
        this.channelIncludes = [
          ...this.channelIncludes,
          ...(((channelResult.included as any) ?? []) as Array<
            Queue | Channel | ChannelConsumer
          >),
        ]
      }
    },
    async loadChannelSessionData() {
      const authStore = useAuthStore()
      const api = useLetsDialAPI()
      if (authStore.user?.id && authStore.selectedOrganization?.id) {
        const channelResult = await api.channelSessions.list({
          filter: {
            userId: authStore.user?.id,
            channelOutcomeId: 'isNull',
            channel: {
              organizationId: authStore.selectedOrganization?.id,
            } as FilterObject,
          },
          include: 'channel,consumer,channel.queue',
          fields: {
            channelSessions: 'additionalData,channel,consumer',
            channels: 'name,platform,queue',
            channelConsumers: 'additionalData,channelUserReference',
            queues: 'name',
          },
        })
        if (channelResult.data && channelResult.data.length > 0) {
          this.channelSessions = channelResult.data.map((session) => {
            if (!session.meta) {
              session.meta = {
                unreadMessages: [],
                lastAction: DateTime.local().toSQL(),
              }
            }
            return session
          })
          this.channelIncludes = ((channelResult.included as any) ??
            []) as Array<Queue | Channel | ChannelConsumer>
          const messages = await api.channelSessionMessages.list({
            filter: {
              channelSessionId: {
                in: channelResult.data.map((item) => item.id).join(','),
              } as FilterObject,
              userMessage: true,
              // readAt: 'isNull',
            },
            fields: {
              channelSessionMessages: 'updatedAt,readAt,session',
            },
            sort: 'updatedAt',
          })
          messages.data?.forEach((message) => {
            if (message.relationships.session.data) {
              const session = this.channelSessions.find(
                (session) =>
                  session.id === message.relationships.session.data?.id,
              )
              if (session) {
                if (!message.attributes.readAt) {
                  console.log(message.attributes)
                  session.meta.unreadMessages.push(message.id)
                }
                session.meta.lastAction = message.attributes.updatedAt
              }
            }
          })
        }
      }
      await this.addListeners()
    },
    async reloadTimedData(count = 0) {
      console.log(`reloadTimedData ${count}`)
      if (count === 4) {
        count = 0
      }
      if (count === 0) {
        await this.loadMissedCallDispositions()
      }

      await this.loadCallbacks()
      await this.loadVoiceMailMessages()
      await this.loadUnassignedMessages()
      timedDataTimer = setTimeout(async () => {
        count++
        this.reloadTimedData(count)
      }, 60000)
    },
    async loadMissedCallDispositions() {
      const authStore = useAuthStore()
      const api = useLetsDialAPI()
      if (authStore.user?.id && authStore.selectedOrganization?.id) {
        const result = await api.calls.list({
          filter: {
            userId: authStore.user?.id,
            campaignDispositionId: 'isNull',
            killed: false,
            organizationId: authStore.selectedOrganization?.id,
            createdAt: 'lastWeek',
            updatedAt: {
              lte: DateTime.local().minus({ minutes: 5 }).toSQL(),
            },
            campaignDisposition: 'existsForCampaign',
          },
          include: 'campaign',
          fields: {
            calls: 'createdAt,inbound,billingSeconds,campaign',
            campaign: 'name',
          },
        })
        if (result.data) {
          this.missedDispositions = result.data
          this.missedDispositionIncludes = ((result.included as any) ??
            []) as Array<Campaign>
        }
      }
    },
    async loadCallbacks() {
      const authStore = useAuthStore()
      const api = useLetsDialAPI()
      if (authStore.user?.id && authStore.selectedOrganization?.id) {
        const result = await api.callbacks.list({
          filter: {
            userId: authStore.user?.id,
            completed: false,
            deletedAt: 'isNull',
            organizationId: authStore.selectedOrganization?.id,
            scheduledFor: {
              lt: DateTime.local().toSQL(),
            },
          },
          include: 'campaignListRecord,campaignListRecord.campaignList',
          fields: {
            callbacks:
              'scheduledFor,note,campaignListRecord,phoneNumber,additionalData',
            campaignListRecords: 'firstName,lastName,campaignList',
          },
        })
        if (result.data) {
          this.callbacks = result.data
          this.callbacksIncludes = ((result.included as any) ?? []) as Array<
            CampaignListRecord | CampaignList
          >
        }
      }
    },
    async loadVoiceMailMessages() {
      const authStore = useAuthStore()
      const api = useLetsDialAPI()
      if (authStore.user?.id && authStore.selectedOrganization?.id) {
        const result = await api.voicemailMessages.list({
          filter: {
            userId: authStore.user?.id,
            completedAt: 'isNull',
            call: {
              organizationId: authStore.selectedOrganization?.id,
            },
          },
          include: 'call,call.queue',
          fields: {
            calls: 'number,queue',
          },
        })
        if (result.data) {
          this.voicemailMessages = result.data
          this.voicemailMessagesIncludes = ((result.included as any) ??
            []) as Array<Queue | Call>
        }
      }
    },
    async loadUnassignedMessages() {
      const authStore = useAuthStore()
      const api = useLetsDialAPI()
      if (
        authStore.user?.id &&
        authStore.selectedOrganization?.id &&
        authStore.user.relationships.queues.data
      ) {
        console.log('loadUnassignedMessages')
        const result = await api.voicemailMessages.list({
          filter: {
            userId: 'isNull',
            completedAt: 'isNull',
            call: {
              organizationId: authStore.selectedOrganization?.id,
              queueId: {
                in: authStore.user.relationships.queues.data
                  .map((q) => q.id)
                  .join(','),
              },
            },
          },
          include: 'call,call.queue',
          fields: {
            calls: 'number,queue',
          },
        })
        if (result.data) {
          this.unassignedVoicemailMessages = result.data
          this.unassignedVoicemailMessagesIncludes =
            ((result.included as any) ?? []) as Array<Queue | Call>
        }
      }
    },
    async unmount() {
      if (authListener) {
        authListener()
      }
      if (timedDataTimer) {
        clearTimeout(timedDataTimer)
      }
      if (channelStreamClose) {
        channelStreamClose()
        channelStreamClose = undefined
      }
      if (voicemailMessagesStreamClose) {
        voicemailMessagesStreamClose()
        voicemailMessagesStreamClose = undefined
      }
      this.$reset()
    },
  },
})
